import secp256k1 from "secp256k1";
import { p2pkh } from "bitcoinjs-lib/src/payments/p2pkh";
import * as bech32 from "bech32";
import ledgerAppLibs from "./libs";
import ERRORS from "./errors";
import utils from "../lib/utils";
import * as lib from "../lib/tendermint";

const SKIP_ERRORS = [
  `Ledger's screensaver mode is on`,
  `The device is already open`,
];
const FAILS_THRESHOLD = 300; // 150 sec
const STATUS_OK = 0x9000;
const eventQueue = utils.createQueue();
let fails = 0;
let ledgerApp = null;

const Ledger = () => {
  const LedgerApp = ledgerAppLibs.terra;
  fails = 0;
  const connect = async ({ skipFailsThreshold }) => {
    if (ledgerApp) {
      const appInfo = await ledgerApp.appInfo();
      if (appInfo.return_code === STATUS_OK) {
        return ledgerApp;
      }
      ledgerApp = null;
    }
    try {
      await navigator.usb.getDevices();
      const app = new LedgerApp();
      await app.connect();
      ledgerApp = app.terraApp;
      return ledgerApp;
    } catch (error) {
      const skipError = SKIP_ERRORS.some((err) =>
        error.message?.startsWith(err)
      );
      if ((skipFailsThreshold && !skipError) || fails > FAILS_THRESHOLD) {
        throw error;
      }
      fails++;
      await utils.sleep(500);
      return connect({ skipFailsThreshold });
    }
  };
  const getPubKey = async (path) => {
    if (!ledgerApp) throw new Error(ERRORS.CANNOT_CONNECT);
    const { compressed_pk: pubKey } = await eventQueue.push(
      ledgerApp.publicKey.bind(ledgerApp, path)
    );
    return pubKey;
  };

  const getAddress = async (path, payload) => {
    const pubkey = await getPubKey(path);
    const rawAddress = p2pkh({ pubkey });
    const words = bech32.toWords(rawAddress.hash);
    const address = bech32.encode(payload.hrpAddress, words);
    return { address, pubkey };
  };

  const sign = async (path, message) => {
    throw new ReferenceError("`sign` is not implemented.");
  };

  const signTransaction = async (path, rawTx, { pubKeyType }) => {
    if (!ledgerApp) throw new Error(ERRORS.CANNOT_CONNECT);
    const pubKey = await getPubKey(path);
    const txMsgs = lib.rawTxtoJson(rawTx);
    const { signature, error_message } = await eventQueue.push(
      ledgerApp.sign.bind(ledgerApp, path, txMsgs)
    );
    if (!signature && error_message) {
      throw new Error(error_message);
    }
    const importedSignature = secp256k1.signatureImport(signature);
    return lib.createSignature(importedSignature, pubKey, pubKeyType);
  };

  return {
    connect,
    getPubKey,
    getAddress,
    sign,
    signTransaction,
  };
};

export default Ledger;
